<template>
  <div v-if="state === 'init'">
    <v-progress-linear
      :indeterminate="true"
      color="primary"
    />
  </div>
  <div v-else-if="state.includes('choose_expert')">
    <choose-lo
      :activation-code="activationCode"
      :state-definition="current_state_definition"
      @choice-made="handleServicerChoice"
      @customize-title="(value) => { titleText = value }"
    />
  </div>
  <div v-else>
    <div
      v-if="current_state_definition.field_header"
      class="field-header"
    >
      {{ current_state_definition.field_header }}
    </div>
    <div
      v-for="(field,index) in current_state_definition.fields"
      :key="'field-' + index"
    >
      <div
        v-if="field.text"
        id="content"
        v-html="field.text"
      />
      <v-radio-group
        v-if="field.options"
        v-model="field.response"
        :mandatory="true"
      >
        <div
          v-for="(option, optionIndex) in field.options"
          :key="'field-' + index + '-option-' + optionIndex"
        >
          <v-radio
            v-if="option.label !== ''"
            :id="'sel_option_' + optionIndex"
            :label="option.label"
            :aria-label="option.label"
            :value="option.value"
            color="primary"
          />
        </div>
      </v-radio-group>
      <v-text-field
        v-if="!field.options && (field.type !== 'phone' && field.type !== 'password' && field.type !== 'checkbox')"
        v-model="field.response"
        :autofocus="index === 0"
        :class="'sel_input_' + index"
        :disabled="field.disabled"
        :error="field.ruleError"
        :error-messages="field.error ? field.error : []"
        :label="field.hint"
        :name="field.hint"
        type="text"
        @focus="clearErrors(index)"
        @blur="checkErrors(index)"
        @keyup.enter="keyupEnter()"
      />
      <template v-if="!field.options && field.type === 'phone'">
        <PhoneInput
          v-model="field.response"
          :error="field.ruleError"
          :error-messages="field.error ? field.error : []"
          :name="field.hint"
          :disabled="field.disabled"
          :label="field.hint"
          color="primary"
          @focus="clearErrors(index)"
          @blur="checkErrors(index)"
          @keyup.enter="keyupEnter()"
        />
        <v-checkbox
          v-if="state === 'auth_validate_phone'"
          v-model="shareToSms"
          class="mt-0 mb-0"
          label="Send me the app invite!"
        />
      </template>
      <PasswordInput
        v-if="field.type === 'password'"
        :id="field.hint"
        v-model="field.response"
        :name="field.hint"
        :disabled="field.disabled"
        :hint="field.requirements"
        :disable-rules="true"
        :label="field.hint"
        :error="field.ruleError"
        color="primary"
        @focus="clearErrors(index)"
        @blur="checkErrors(index)"
        @keyboard-enter="keyupEnter()"
      />
      <v-checkbox
        v-if="field.type === 'checkbox'"
        :id="field.hint"
        v-model="field.response"
        class="mt-0 mb-0"
        :label="field.label"
      />
    </div>
    <p
      v-if="error"
      class="red--text"
    >
      {{ errorMessages ? errorMessages[0] : '' }}
    </p>
    <v-layout
      class="mt-4 ml-0"
      xs12
      align-center
    >
      <v-flex
        v-for="(action, index) in actions"
        :key="'action-' + index"
        shrink
      >
        <v-btn
          v-if="action.label && index === 0"
          :outline="action !== current_state_definition.primary_action"
          :class="'primary white--text btn--large sel_button_' + index"
          :loading="waitingRequest"
          :style="{backgroundColor: buttonColor}"
          :aria-label="action.label"
          @click="nextState(action);"
        >
          {{ action.label }}
        </v-btn>
        <a
          v-else-if="action.label"
          @click="nextState(action)"
        >
          {{ action.label }}
        </a>
      </v-flex>
    </v-layout>
  </div>
</template>

<script>
import snValidationsService from '../../services/snValidationsService.js'
import chooseLo from './chooseLo.vue'
import phoneInput from './snPhoneInput.vue'
import passwordInput from './snPasswordInput.vue'
import cookie from 'js-cookie'
import shareBySmsMixin from './shareBySmsMixin.js'

export default {

  name: 'StatefulAuth',

  components: {
    PhoneInput: phoneInput,
    PasswordInput: passwordInput,
    ChooseLo: chooseLo
  },

  mixins: [shareBySmsMixin],

  props: {
    activationCode: {
      type: String,
      default: '',
      required: false
    },
    autoLogin: {
      type: Boolean,
      default: false,
      required: false
    },
    userEmail: {
      type: String,
      default: '',
      required: false
    },
    init: {
      type: String,
      default: '',
      required: false
    }
  },
  data () {
    return {
      email: null,
      token: null,
      current_state_definition: { fields: [] },
      actions: {},
      state: 'init',
      titleText: '',
      subtitleText: '',
      values: [],
      error: false,
      errorMessages: [],
      waitingRequest: false,
      buttonColor: '#003a5d',
      shareToSms: false,
      rules: {
        email: (value) => {
          return snValidationsService.email(value) || 'Invalid e-mail'
        },
        phone: (value) => {
          return snValidationsService.phone(value) || 'Invalid phone number'
        },
        integer: (value) => {
          if (!value) return true
          const pattern = /^\d+/
          return pattern.test(value) || 'Invalid number'
        },
        name: (value) => {
          return true
        }
      }
    }
  },

  // Usually the titles for each state are displayed in a parent component so this controls that display
  watch: {
    titleText () {
      this.$emit('title', this.titleText)
    },
    subtitleText () {
      this.$emit('subtitle', this.subtitleText)
    },
    state (newVal) {
      this.$emit('change-state', this.state)
      if (this.state === 'auth_sign_in' && this.$route && this.$route.query.email) {
        this.current_state_definition.fields[0].response = this.$route.query.email
      }
    },
    init (newVal) {
      if (newVal === 'start') {
        this.getStarted(true)
      }
    }
  },

  created () {
    this.sn_shareBySmsMixin_showSnackbarMessages = false
    this.getStarted(this.init === 'start')
    if (this.autoLogin) {
      if (window.PasswordCredential) {
        navigator.credentials.get({
          password: true,
          mediation: 'required'
        }).then(c => {
          if (c) {
            this.current_state_definition.fields[0].response = c.id
            this.current_state_definition.fields[1].response = c.password
            this.nextState()
          }
        }).catch(error => {
          // various browsers throw a NotSupportedError if they don't support credentials
          console.log('credential management is not supported')
          return error
        })
      }
    }
  },

  methods: {
    keyupEnter () {
      if (!this.waitingRequest) {
        this.nextState()
      }
    },

    handleServicerChoice (actionValue, selectedSac) {
      const action = {
        key: 'event',
        value: actionValue
      }
      if (actionValue === 'save_servicer') {
        this.current_state_definition.fields[0].response = selectedSac
        this.activationCode = selectedSac
        this.$emit('change-activation-code', selectedSac)
        this.nextState(action)
      }
      if (actionValue === 'skip_selection') {
        this.current_state_definition.fields[0].response = this.activationCode
        this.nextState(action)
      }
    },

    createRequest (action) {
      // This function goes through the current state definition and creates a request
      // based on keys and values and the event based on the action passed to this function
      const request = {
        from_web: true
      }
      request.auth_guid = cookie.get('auth_guid')
      if (this.activationCode) { // pass this through as soon as possible otherwise redirect to borrower/signup
        request.activation_code = this.activationCode
      }
      if (this.state.includes('choose_expert')) {
        request.event = action.value
        request.activation_code = this.activationCode
        return request
      }
      if (action) {
        request.event = action.value
        if (this.current_state_definition.fields) {
          for (let x = 0; x < this.current_state_definition.fields.length; x++) {
            request[this.current_state_definition.fields[x].key] = this.current_state_definition.fields[x].response
          }
        }
      }
      return request
    },

    checkErrors (index, checkUndefined) {
      checkUndefined = false
      // Check the rules for different types of fields for validation of input

      // Check to see if we are looking at valid field
      if (this.current_state_definition.fields && this.current_state_definition.fields[index] === undefined) {
        return false
      }

      // Trim imput
      if (this.current_state_definition.fields[index].response !== '' && this.current_state_definition.fields[index].response !== undefined) this.current_state_definition.fields[index].response = this.current_state_definition.fields[index].response.trim()

      // Run custom validation
      if (this.rules[this.current_state_definition.fields[index].type]) {
        if (this.rules[this.current_state_definition.fields[index].type](this.current_state_definition.fields[index].response) !== true) {
          this.current_state_definition.fields[index].ruleError = true
          this.current_state_definition.fields[index].error = this.rules[this.current_state_definition.fields[index].type](this.current_state_definition.fields[index].response)
          return true
        }
      }
      return false
    },

    clearErrors (index) {
      this.$set(this.current_state_definition.fields[index], 'ruleError', false)
      if (this.current_state_definition.fields[index].response === undefined) {
        this.current_state_definition.fields[index].response = ''
      }

      let removeErrorMessage = true
      for (let x = 0; x < this.current_state_definition.fields.length; x++) {
        if (this.current_state_definition.fields[x].ruleError === true) {
          removeErrorMessage = false
        } else {
          this.current_state_definition.fields[x].error = ''
        }
      }
      if (removeErrorMessage === true) {
        this.errorMessages = []
      }
    },

    // Redirect to sessions controller with token
    finishedAuthentication () {
      this.$axios.post('/sessions', {
        token: this.token,
        email: this.email,
        authenticity_token: document.getElementsByName('csrf-token')[0].content,
        fromLogin: true,
        fromMobileFlow: true,
        sac_id: this.sacId,
        user_id: this.userId
      })
        .then(
          (response) => {
            window.location = response.data
          }
        )
        .catch(
          (error) => {
            this.$sentry.captureException(error)
            console.log(error)
          }
        )
    },

    ping () {
      const url = '/auth/v1/ping/' + cookie.get('auth_guid')
      this.$axios.get(url)
        .then(
          (response) => {
            if (response.data.current_state !== 'auth_waiting_for_verification') {
              clearInterval(this.pingFunction) // Once we have moved forward in the flow this stops the ping requests from continuing
              if (this.state === 'auth_waiting_for_verification') {
                this.$axios.post('/auth/v1/sync', this.createRequest(null))
                  .then((response) => {
                    this.processAuthResponse(response)
                  })
              }
            }
          }
        )
    },

    nextState (action) {
      if (this.shareToSms && this.state === 'auth_validate_phone') {
        this.sn_shareBySmsMixin_shareAppBySms(this.activationCode, this.current_state_definition.fields[0].response)
      }

      if (action === undefined || action === null || action === '') {
        action = this.current_state_definition.primary_action
      }
      if (this.current_state_definition.fields && this.state !== 'auth_waiting_for_verification' && this.state.includes('choose_expert') && action === this.current_state_definition.primary_action) {
        let errorsExist = false
        for (let x = 0; x < this.current_state_definition.fields.length; x++) {
          // Make sure all the fields have input
          if (!this.current_state_definition.fields[x].response) {
            this.$set(this.current_state_definition.fields[x], 'ruleError', true)
            errorsExist = true
            this.error = true
            this.errorMessages = ['All fields are required']
          } else { // If any fields still have errors don't move to the next state
            errorsExist = this.checkErrors(x, true)
          }
        }
        if (errorsExist) {
          return
        }
      }
      this.waitingRequest = true // start showing spinner
      this.$axios.post('/auth/v1/sync', this.createRequest(action))
        .then(
          (response) => {
            this.processAuthResponse(response)
          }
        )
    },

    // Currently we are skipping the init state used on mobile
    getStarted (init = false) {
      cookie.set('new_user', true)
      this.$axios.post('/auth/v1/init', { from_web: true })
        .then(
          (response) => { // this could be changed if we switch to the full state machine
            // we are using set instead of setting equal to in order to allow vue to responsively react to the changes of the values within the keys.
            // for more info: https://vuejs.org/v2/guide/reactivity.html#Change-Detection-Caveats
            const keys = Object.keys(response.data.global_action_definitions)
            keys.forEach(key => {
              this.$set(this.current_state_definition, key, response.data.global_action_definitions[key])
            })
            cookie.set('auth_guid', response.data.values.auth_guid)
            if (init) {
              this.nextState(response.data.global_action_definitions.primary_action)
            } else {
              this.nextState(response.data.global_action_definitions.secondary_action)
            }
          }
        )
    },

    setActions () {
      this.actions = []
      if (this.current_state_definition.primary_action) {
        this.actions = [this.current_state_definition.primary_action] // These are the buttons that are displayed
      }
      if (this.current_state_definition.secondary_action) {
        this.actions = this.actions.concat(this.current_state_definition.secondary_action)
      }
      if (this.current_state_definition.additional_actions) { // If there are additional actions besides main action
        this.actions = this.actions.concat(this.current_state_definition.additional_actions)
      }
    },

    setFields () {
      for (let x = 0; x < this.current_state_definition.fields.length; x++) {
        this.current_state_definition.fields[x].response = this.values[this.current_state_definition.fields[x].key] // set the response to default values
      }
    },

    setErrors () {
      this.errorMessages = [this.current_state_definition.error]
      if (this.errorMessages && this.errorMessages[0] !== '') this.error = true
      else {
        this.error = false
        this.errorMessages = []
      }
    },

    processAuthResponse (response) {
      // Set current state
      this.state = response.data.current_state
      // the state will be expired if the auth has completed or a time out, so the check for authenticated_data is necessary
      if (this.state === 'init') {
        this.$emit('title', 'With a little information, we\'ll save your work for you.')
        this.$emit('change-local-state', 'initial')
        return
      }
      if (this.state === 'auth_signed_in' && response.data.authenticated_data) {
        const payload = {
          eventName: 'User Logged In',
          currentUserGuid: response.data.authenticated_data.user.guid,
          properties: {
            email: response.data.authenticated_data.user.email,
            name: response.data.authenticated_data.user.name
          }
        }
        this.$snac.logToSegment('userLoggedIn', payload)

        this.email = response.data.authenticated_data.user.email
        this.token = response.data.authenticated_data.token
        this.sacId = response.data.authenticated_data.sac_id
        this.userId = response.data.authenticated_data.user.id
        this.finishedAuthentication()
        return
      }
      // new_inputs_combined comes back if we don't recognize the email, activationCode is blank if we don't recognize the servicer
      // so we redirect them so they enter the servicer and then they can continue
      if (this.state === 'new_inputs_combined' && (!this.$route || !this.$route.path.includes('onboarding')) && this.activationCode !== '') {
        window.location.href = `/borrower/signup/${this.activationCode}`
        return
      } else if (this.state === 'new_inputs_combined' && this.activationCode === '') {
        window.location.href = '/borrower/signup/'
        return
      } else if (this.state === 'new_inputs_combined' && this.activationCode !== response.data.values.activation_code) {
        this.activationCode = response.data.values.activation_code
      } else if (this.state === 'new_complete') {
        this.nextState({
          value: 'complete'
        })
        return
      }

      this.values = response.data.values // gets current preset values such as email, phone name ect. These are displayed as helps to user if fields match
      this.current_state_definition = response.data.current_state_definition || { fields: [] }
      this.setActions()
      this.setFields()
      this.setErrors()
      this.titleText = this.current_state_definition.title
      this.subtitleText = this.current_state_definition.subtitle
      this.fieldHeader = this.current_state_definition.field_header
      // If we are waiting for verification we starting pinging so if they click on the email link we can advance
      if (this.state === 'auth_waiting_for_verification') {
        this.pingFunction = setInterval(function () {
          this.ping()
        }.bind(this), response.data.current_state_definition.refresh_rate)
      }
      // If we are passed the email from another component already (such as borrowerInitial) this will move us forward in the state machine.
      if (this.userEmail && this.state === 'new_input_email' && response.data.success === true) {
        this.current_state_definition.fields[0].response = this.userEmail
        this.state = 'init'
        this.nextState()
      }
      this.waitingRequest = false // stop displaying the spinner next to action button
      // The following if statement is to remove the loading bar once the component is set up
    }
  }

}
</script>

<style scoped>
#content {
  max-height: 45vh;
  margin-bottom: 25px;
  overflow-y: auto;
  padding-left: 5px;
}
.btn {
  margin: 0px !important;
}
.field-header {
  margin-top: 16px;
  font-weight: bold;
  font-size: 15px;
}
</style>
